import React from 'react'
import { Box, Stack } from '@mui/material'
import { Skeleton } from '@repo/ui'
import { useTheme } from '@mui/material/styles'
import { STATUS_TOKEN_H } from 'components'

import { PROSE_LINE_H, ProseSkeleton } from '../ProseSkeleton'

/**
 * Loading state for /whats-new (§19: skeletons, never spinners, and the skeleton
 * occupies the layout the content will land in).
 *
 * Mirrors an `AnnouncementCard` region for region — kind chip + date row, title,
 * three-line body, then the CTA button — inside a box with the SAME padding, border and
 * radius as the real card. That footprint parity is the rule: a shimmering block of
 * roughly the right size still shifts the page when it is replaced.
 *
 * ## Every height here is a measured one
 *
 * All four regions were wrong before, in both directions, and they did not cancel: the card
 * came out 198px against a real 223.4, so each card below it landed 18.6px off and the error
 * compounded down the feed. What the numbers are and where they come from:
 *
 *   - kind chip — `STATUS_TOKEN_H` (28), the constant `AnnouncementCard` itself uses. Was 24.
 *   - title — 22.4, `body2` at the card's `lineHeight: 1.4`. Was 28.
 *   - CTA — 32, the height of a `size="small"` Button. Was 36.
 *   - body — TWO lines via `ProseSkeleton`, which reserves the real 42px rather than the 32 a
 *     bare `<Skeleton lines={2}>` does. Why two is not obvious; see below.
 *
 * ## The body line count is chosen to minimise CUMULATIVE drift, not per-card error
 *
 * Card height varies with the body's line count (the feed clamps at three) and with whether the
 * announcement carries a CTA or a "You asked for these" strip. Measured across the eight
 * published announcements at this column width: 137.4, 137.4, 179.4, 181.4, 223.4, 223.4, 223.4,
 * 281.8 — mean 198.5, median 202.4, mode 223.4.
 *
 * The instinct is to draw the mode. That is wrong for a STACK: card n's position depends on the
 * SUM of the heights above it, so the error a reader sees compounds down the feed and the right
 * statistic is the central one, not the most frequent. Summing the absolute error of the two card
 * positions this skeleton actually predicts (it draws three cards, so the 2nd and 3rd tops):
 * mode (223.4) is 88px off, the shortest card (179.4) is 44px, and the median (202.4) is 25px.
 *
 * A two-line body plus a CTA comes to exactly 202.4. No individual announcement is that shape —
 * it is the height that leaves the feed's cards closest to where they will land.
 *
 * Be clear about what that did and did not buy, because the old numbers were accidentally decent:
 * the previous card came to 198, which is near the mean, so cumulative drift barely moved (25.4px
 * → 25px). The win here is INSIDE the card. Every region above was independently wrong, so the
 * card's own contents re-flowed on load even when the card's total happened to land close.
 *
 * The remaining sources of shift are not knowable before the response arrives and are left alone
 * deliberately: how MANY announcements this reader has, and the "You asked for these" strip.
 */
export const WhatsNewPageSkeleton = () => {
  const theme = useTheme()

  return (
    // The page wrapper carries the column cap, so this inherits it — and therefore
    // occupies the same footprint the loaded feed will (§19).
    <Stack spacing={4}>
      {[0, 1, 2].map(i => (
        <Box
          key={i}
          sx={{
            p: 5,
            borderRadius: 2,
            border: `1px solid ${theme.palette.primary[100]}`,
            bgcolor: 'background.paper',
          }}
        >
          <Stack spacing={3}>
            {/* Kind chip left, date hard right — `justifyContent: space-between`, as the card. */}
            <Stack direction="row" alignItems="center" justifyContent="space-between">
              <Skeleton width={84} height={STATUS_TOKEN_H} variant="rounded" />
              <Skeleton width={88} height={18} />
            </Stack>
            {/* Title */}
            <Skeleton width="45%" height={PROSE_LINE_H.body2Title} />
            {/* Body — two lines, the stack-median choice explained above */}
            <ProseSkeleton lines={2} />
            {/* CTA */}
            <Skeleton width={132} height={32} variant="rounded" />
          </Stack>
        </Box>
      ))}
    </Stack>
  )
}
